import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { http, downloadReadersCsv, errMsg } from "../lib/api";
import { useAuth, PageLoading } from "../context/AuthContext";
import { BookCover, Metric, Stamp } from "../components/Bits";

export const KIT_KEYS = ["hook", "kdp_keywords", "blurb_back", "bio_medium", "pitch_bloggers"];
export const kitDone = (b) => KIT_KEYS.filter((k) => b.kit?.[k]?.length).length;
const clicks = (b) => Object.values(b.stats?.clicks || {}).reduce((a, n) => a + n, 0);

function BookRow({ b }) {
  const plan = b.plan || [];
  const done = plan.filter((p) => p.done).length;
  return (
    <Link to={`/books/${b.id}`} className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[5rem_1fr_auto] gap-5 sm:gap-8 py-7 border-t border-[#E5DEC9] group items-center" data-testid={`dashboard-book-row-${b.id}`}>
      <BookCover src={b.cover_url} title={b.title} className="w-full group-hover:-rotate-2 transition-transform" />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Stamp active={b.published}>{b.published ? "Published" : "Draft"}</Stamp>
          {b.hide_from_discover && <Stamp>Hidden from shelf</Stamp>}
        </div>
        <h3 className="font-display text-2xl sm:text-3xl mt-3 truncate group-hover:text-[#C84B31] transition-colors">{b.title}</h3>
        <p className="font-type text-[0.7rem] uppercase tracking-wider text-[#5C584E] mt-2">
          Kit {kitDone(b)}/5 · Plan {plan.length ? `${done}/${plan.length}` : "not started"}
        </p>
      </div>
      <div className="col-span-2 sm:col-span-1 grid grid-cols-4 gap-5 text-right font-type text-xs">
        {[["Views", b.stats?.views || 0], ["Shelf", b.stats?.discover_views || 0], ["Clicks", clicks(b)], ["Readers", b.stats?.signups || 0]].map(([l, v]) => (
          <div key={l}><p className="text-[#8E8879] uppercase text-[0.6rem] tracking-wider">{l}</p><p className="font-display text-2xl">{v}</p></div>
        ))}
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [books, setBooks] = useState(null);

  useEffect(() => {
    http.get("/books").then(({ data }) => setBooks(data));
  }, []);

  const exportAll = () => downloadReadersCsv().catch((e) => toast.error(errMsg(e)));
  if (!books) return <PageLoading />;
  const sum = (f) => books.reduce((a, b) => a + f(b), 0);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <p className="label-mono">Your desk</p>
          <h1 className="font-display text-5xl tracking-tight mt-2">Good to see you, <span className="italic">{user.name?.split(" ")[0]}</span>.</h1>
          <p className="mt-3 text-[#5C584E]">Your author page: <Link to={`/a/${user.slug}`} className="border-b border-[#121210] hover:text-[#C84B31]" data-testid="dashboard-author-page-link">/a/{user.slug}</Link></p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button className="btn-secondary" onClick={exportAll} data-testid="dashboard-export-readers-button"><Download size={13} /> Readers CSV</button>
          <Link to="/books/new" className="btn-primary" data-testid="dashboard-new-book-button"><Plus size={14} /> New book</Link>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mt-14">
        <Metric label="Books" value={books.length} testid="dashboard-metric-books" />
        <Metric label="Page views" value={sum((b) => b.stats?.views || 0)} testid="dashboard-metric-views" />
        <Metric label="Shelf views" value={sum((b) => b.stats?.discover_views || 0)} testid="dashboard-metric-discover" />
        <Metric label="Store clicks" value={sum(clicks)} testid="dashboard-metric-clicks" />
        <Metric label="Readers" value={sum((b) => b.stats?.signups || 0)} testid="dashboard-metric-readers" />
      </div>
      <section className="mt-16">
        {books.length === 0 ? (
          <div className="border border-dashed border-[#121210] p-12 sm:p-16 text-center" data-testid="dashboard-empty">
            <p className="font-hand text-3xl text-[#C84B31]">The desk is clear.</p>
            <h2 className="font-display text-3xl mt-2">Add your first book and get its launch kit.</h2>
            <Link to="/books/new" className="btn-primary mt-8" data-testid="dashboard-empty-new-book-button"><Plus size={14} /> Add a book</Link>
          </div>
        ) : (
          <div className="border-b border-[#E5DEC9]">{books.map((b) => <BookRow key={b.id} b={b} />)}</div>
        )}
      </section>
    </div>
  );
}
